import React, { useEffect, useState, useMemo } from "react";
import { Link } from "react-router-dom";
import Cookies from "universal-cookie";
import { useTable, usePagination, useSortBy } from "react-table";
import CambiaEstado from "../Evaluador/CambiaEstado";
import Swal from "sweetalert2";
import InformesRepositorio from "../ObtenerInforme";
import Avance from "../Evaluador/Avance";
import ResumenOm from "../CargaOm/ResumenOm";
import ResumenOmModal from "../CargaOm/ResumenModal";

const cookies = new Cookies();

function SolicitudesJunta({ correoDesencriptado }) {
  const [usuarioId, setUsuarioId] = useState(null);
  const [solicitud, setSolicitud] = useState([]);
  const [search, setSearch] = useState("");

  const basicAuthHeader =
    "Basic " + btoa("BasicAuth@icontec.org:yQLzqmR7eZSuAmQZUqhY");

  const urlendpoint =
    "https://suadesarrollo.azurewebsites.net/api/JuntaDirectiva/ObtenerQSolicitudesUsuario/" +
    cookies.get("id") +
    "/1";

  // 🔎 Obtener usuario
  useEffect(() => {
    if (!correoDesencriptado) return;

    const obtenerUsuario = async () => {
      try {
        const response = await fetch(
          `https://suadesarrollo.azurewebsites.net/api/obtenerIdUsuario?correo=${correoDesencriptado}`,
          { headers: { Authorization: basicAuthHeader } },
        );

        if (!response.ok) throw new Error("Error HTTP");
        const data = await response.json();

        if (!cookies.get("usuarioId")) {
          Swal.fire({
            title: `👋 Bienvenido ${data.nombre}`,
            text: "Estamos cargando tu información",
            icon: "success",
          }).then(() => {
            cookies.set("usuarioId", data.id, { path: "/" });
            cookies.set("id", data.id, { path: "/" });
            cookies.set("Nombre", data.nombre, { path: "/" });
            cookies.set("correo", data.correo_Electronico, { path: "/" });
            cookies.set("tipo_Usuario", data.tipo_Usuario, { path: "/" });
            cookies.set("institucionid", data.institucionId, { path: "/" });

            setUsuarioId(data.id);
          });
        } else {
          setUsuarioId(cookies.get("usuarioId"));
        }
      } catch (error) {
        console.error("Error usuario:", error);
        Swal.fire("Error", "No se pudo obtener el usuario", "error");
      }
    };

    obtenerUsuario();
  }, [correoDesencriptado]);

  // 🔄 Obtener solicitudes
  useEffect(() => {
    const showData = async () => {
      try {
        const response = await fetch(urlendpoint, {
          headers: { Authorization: basicAuthHeader },
        });

        const data = await response.json();

        if (Array.isArray(data)) {
          setSolicitud(data);
        } else if (Array.isArray(data.resultado)) {
          setSolicitud(data.resultado);
        } else {
          setSolicitud([]);
        }
      } catch (error) {
        console.error("Error al obtener datos:", error);
        setSolicitud([]);
      }
    };

    showData();
  }, []);

  // 🔍 Buscador
  const searcher = (e) => setSearch(e.target.value);

  const results = useMemo(() => {
    if (!Array.isArray(solicitud)) return [];
    return !search
      ? solicitud
      : solicitud.filter((dato) =>
          dato.nombreInstitucion?.toLowerCase().includes(search.toLowerCase()),
        );
  }, [search, solicitud]);

  const tipoUsuario = Number(cookies.get("tipo_Usuario"));

  // 📌 Columnas
  const columns = useMemo(() => {
    let baseColumns = [
      { Header: "N° Solicitud", accessor: "solicitud" },
      { Header: "Nombre", accessor: "nombreInstitucion" },
      { Header: "Nit", accessor: "nitInstitucion" },
      { Header: "Estado", accessor: "estado" },
      { Header: "Tipo Solicitud", accessor: "tipoSolicitud" },
    ];

    baseColumns.push(
      {
        Header: "Anexos",
        Cell: ({ row }) => (
          <Link to={`/RepositorioEvaluador/${row.original.institucionId}`}>
            Anexos
          </Link>
        ),
      },
      {
        Header: "Informes PDF",
        Cell: ({ row }) => {
          const estado = row.original.estado?.toLowerCase();
          const bloqueados = [
            "cancelada",
            "en diligenciamiento",
            "en revision",
            "en revisión",
          ];

          return tipoUsuario !== 1 && !bloqueados.includes(estado) ? (
            <InformesRepositorio
              idSolicitud={row.original.solicitud}
              idinstitucion={row.original.institucionId}
            />
          ) : null;
        },
      },
      {
        Header: "Informe Evaluador",
        Cell: ({ row }) => (
          <Link
            to={`/InformeEvaluador/${row.original.institucionId}/${row.original.solicitud}`}
          >
            Informe Evaluador
          </Link>
        ),
      },
      {
        Header: "Informe Autoevaluación",
        Cell: ({ row }) => (
          <Link
            to={`/InformeAuto/${row.original.institucionId}/${row.original.solicitud}`}
          >
            Informe Autoevaluación
          </Link>
        ),
      },
      {
        Header: "Resumen OM",
        Cell: ({ row }) => (
          <ResumenOmModal institucionId={row.original.institucionId} />
        ),
      },
      {
        Header: "Comentatios",
        Cell: ({ row }) => (
          <Link to={`/ComentariosJunta/${row.original.solicitud}`}>
            Comentarios
          </Link>
        ),
      },
    );

    return baseColumns;
  }, [tipoUsuario]);

  const tableInstance = useTable(
    { columns, data: results, initialState: { pageSize: 5 } },
    useSortBy,
    usePagination,
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    prepareRow,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    state: { pageIndex },
    pageOptions,
  } = tableInstance;

  return (
    <div className="card shadow mb-4">
      <div className="card-header">
        <h6 className="m-0 font-weight-bold text-primary">Solicitudes</h6>
      </div>

      <div className="card-body">
        <input
          value={search}
          onChange={searcher}
          placeholder="Buscar por institución"
          className="form-control mt-3"
        />

        <table {...getTableProps()} className="table mt-3 table-bordered">
          <thead>
            {headerGroups.map((hg) => (
              <tr {...hg.getHeaderGroupProps()}>
                {hg.headers.map((col) => (
                  <th {...col.getHeaderProps(col.getSortByToggleProps())}>
                    {col.render("Header")}
                  </th>
                ))}
              </tr>
            ))}
          </thead>

          <tbody {...getTableBodyProps()}>
            {page.map((row) => {
              prepareRow(row);
              return (
                <tr {...row.getRowProps()}>
                  {row.cells.map((cell) => (
                    <td {...cell.getCellProps()}>{cell.render("Cell")}</td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>

        <div className="d-flex justify-content-between">
          <button
            onClick={previousPage}
            disabled={!canPreviousPage}
            className="btn btn-secondary"
          >
            Anterior
          </button>

          <span>
            Página {pageIndex + 1} de {pageOptions.length}
          </span>

          <button
            onClick={nextPage}
            disabled={!canNextPage}
            className="btn btn-secondary"
          >
            Siguiente
          </button>
        </div>
      </div>
    </div>
  );
}

export default SolicitudesJunta;
